import type React from "react";
import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ACCENT_ORANGE } from "./colors";
import { AmbientBackground } from "./visuals/AmbientBackground";
import type { SlideContext } from "./slide.types";
import type { SlideDeck } from "./useSlideDeck";

interface PresentShellProps {
  deck: SlideDeck;
  context: Omit<SlideContext, "resetKey">;
  onExit: () => void;
}

export function PresentShell({ deck, context, onExit }: PresentShellProps): React.JSX.Element {
  const { slides, currentIndex, goNext, goPrev, goTo } = deck;
  const active = slides[currentIndex];

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      switch (e.key) {
        case "Escape":
          onExit();
          break;
        case "ArrowRight":
        case "PageDown":
          goNext();
          break;
        case "ArrowLeft":
        case "PageUp":
          goPrev();
          break;
        case " ":
          e.preventDefault();
          if (e.shiftKey) goPrev();
          else goNext();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [goNext, goPrev, onExit]);

  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto text-white"
      style={{ background: "#000", animation: "pmOverlayIn 300ms ease-out" }}
    >
      <AmbientBackground />

      <div className="pm-content">
        <div
          className="flex items-start justify-between px-10 pt-6 pb-4"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 93.5 110.5" className="w-7 h-7" aria-hidden="true" fill="#f7f7f7">
              <circle cx="29.22" cy="46.75" r="5.84"/>
              <circle cx="46.75" cy="46.75" r="5.84"/>
              <circle cx="64.28" cy="46.75" r="5.84"/>
              <path d="M79.81,13.69C71.34,5.23,59.66,0,46.75,0S22.15,5.23,13.69,13.69C5.24,22.15,0,33.84,0,46.75s5.24,24.6,13.69,33.06l29.03,29.03c2.23,2.23,5.84,2.23,8.06,0l29.03-29.03c8.45-8.46,13.69-20.15,13.69-33.06s-5.24-24.6-13.69-33.06ZM46.75,85.44c-21.37,0-38.7-17.32-38.7-38.7S25.38,8.05,46.75,8.05s38.7,17.32,38.7,38.7-17.32,38.7-38.7,38.7Z"/>
            </svg>
            <div>
              <p className="font-head font-extrabold text-white text-xl leading-tight">
                {context.clientName}
              </p>
              <p className="text-white/40 text-xs font-body tracking-wide">
                {context.periodLabel}
                {active ? ` · ${active.title}` : ""}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onExit}
            className="text-white/40 hover:text-white transition-colors flex items-center gap-2 text-[11px] font-body tracking-wide uppercase"
            aria-label="Close present mode"
          >
            <span>Exit</span>
            <span className="border border-white/20 rounded px-1.5 py-0.5 text-[10px]">Esc</span>
          </button>
        </div>

        <div key={currentIndex} className="px-10 pb-28 max-w-[1400px] mx-auto">
          {active
            ? active.render({ ...context, resetKey: currentIndex })
            : <EmptyDeck />}
        </div>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-10 px-10 py-5"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)",
        }}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2.5">
            {slides.map((s, i) => {
              const isActive = i === currentIndex;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { goTo(i); }}
                  className="group flex flex-col items-center gap-1.5 transition-all"
                  aria-label={`Go to ${s.title}`}
                >
                  <span
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: isActive ? 28 : 8,
                      background: isActive ? ACCENT_ORANGE : "rgba(255,255,255,0.25)",
                    }}
                  />
                  <span
                    className={`text-[10px] font-body uppercase tracking-wider transition-colors ${
                      isActive ? "text-white/60" : "text-white/25 group-hover:text-white/40"
                    }`}
                  >
                    {s.title}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={goNext}
            disabled={currentIndex === slides.length - 1}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <p className="text-center text-white/20 text-[10px] font-body uppercase tracking-[0.2em] mt-3">
          ← → to navigate · Esc to exit
        </p>
      </div>
    </div>
  );
}

function EmptyDeck(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-96 text-white/40 text-sm font-body">
      No slides for this period.
    </div>
  );
}
